import { CaretDown } from '@phosphor-icons/react'
import { Button, type RefSelectProps, Select, type SelectProps } from 'antd'
import { DefaultOptionType } from 'antd/lib/select'
import { debounce } from 'lodash'
import { UIEvent, useCallback, useMemo, useRef, useState } from 'react'

import { SelectOptionModel } from '@/domain/model/select'
import { useLoadAsyncSelect } from '@/queries/asyncSelect/useLoadAsyncSelect'
import Ui from '@/ui'
import ErrorLabel from '@/ui/atoms/ErrorLabel'
import { getFilterOption } from '@/utils/getFilterOption/getFilterOption'
import { normalizeTextToParam } from '@/utils/normalizeTextToParam'
import cn from '@/utils/tailwind/cn'

type Props = {
  fetchOptions: <T>(params?: T) => Promise<SelectOptionModel[]>
  queryKey: string[]
  othersParams?: Record<string, string | number | undefined>
  debounceTimeOut?: number
  searchKey: string
  loadOnMount?: boolean
  isOfficeUse?: boolean
  isRequired?: boolean
  label?: string
  errorLabel?: string
  hasError?: boolean
  notFoundContentAction?: boolean
  hasLocked?: boolean
  infiniteScroll?: boolean
  createOption?: boolean
} & Omit<SelectProps, 'onSearch'>

export const FormAsyncSelect = ({
  fetchOptions,
  queryKey,
  searchKey,
  othersParams,
  debounceTimeOut = 700,
  value,
  notFoundContent,
  notFoundContentAction,
  loadOnMount,
  isOfficeUse,
  isRequired,
  label,
  errorLabel,
  hasError,
  hasLocked,
  disabled,
  className,
  onChange,
  infiniteScroll,
  createOption,
  ...props
}: Props) => {
  const selectRef = useRef<RefSelectProps | null>(null)
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  const [isLocked, setIsLocked] = useState(false)
  const {
    data: optionsResponse,
    isLoading,
    isSuccess,
    isFetching,
    fetchNextPage,
  } = useLoadAsyncSelect(
    fetchOptions,
    [...(queryKey || [])],
    {
      ...{
        ...othersParams,
        [searchKey]: normalizeTextToParam(searchValue || ''),
      },
    },
    { enabled: loadOnMount || !!searchValue?.length }
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((e: string) => setSearchValue(e), debounceTimeOut),
    [debounceTimeOut]
  )

  const onSearch = (value: string) => debouncedSearch(value)

  const handleChange = (
    value: any,
    option?: DefaultOptionType | DefaultOptionType[]
  ) => {
    if (onChange) {
      onChange(value, option)
    }

    if (hasLocked && value) {
      setIsLocked(true)
    }
  }

  const handleClear = () => {
    setIsLocked(false)
    setSearchValue(undefined)
    onChange?.({}, {})
    setTimeout(() => {
      selectRef?.current?.focus()
    }, 100)
  }

  const handleCreate = () => {
    if (!searchValue) return

    const newOption = {
      label: searchValue,
      value: searchValue,
      [props?.optionLabelProp || '']: searchValue,
      is_created: true,
    }

    if (onChange) {
      if (props.labelInValue) {
        onChange(
          {
            ...newOption,
            value: newOption.value,
            label: newOption.label,
          },
          newOption
        )
      } else {
        onChange(newOption.value, newOption)
      }
      if (hasLocked) {
        setIsLocked(true)
      }
      selectRef?.current?.blur()
    }
  }

  const handlePopupScroll = async (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, offsetHeight } = e.currentTarget
    const isNearBottom = scrollTop + offsetHeight === scrollHeight

    if (isNearBottom && !isFetching) {
      await fetchNextPage()
    }
  }

  let notFoundContentValidation = notFoundContent

  if ((isLoading || isFetching) && searchValue?.length) {
    notFoundContentValidation = 'Searching...'
  }

  if ((isLoading || isFetching) && !searchValue?.length) {
    notFoundContentValidation = 'Type to search...'
  }
  const data = useMemo(() => {
    const result = []
    if (optionsResponse?.pages) {
      for (const page of optionsResponse.pages) {
        result.push(...page)
      }
    }
    return result
  }, [optionsResponse])

  if (
    isSuccess &&
    !isFetching &&
    !isLoading &&
    searchValue?.length &&
    notFoundContentAction &&
    !data?.length &&
    createOption
  ) {
    notFoundContentValidation = (
      <div className="flex w-full flex-col items-center justify-center p-2">
        <div className="mb-2">No options found</div>
        <button className="text-primary hover:underline" onClick={handleCreate}>
          Click to Create "{searchValue}"
        </button>
      </div>
    )
  }

  const displayValue = isFetching ? 'Loading...' : value
  return (
    <div className="relative flex flex-col">
      {label && (
        <Ui.QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
          {label}
        </Ui.QuestionTitle>
      )}
      {isLocked && (
        <Button
          className="absolute right-0 top-0 h-fit p-0 !outline-none"
          onClick={handleClear}
          type="link"
        >
          Clear
        </Button>
      )}
      <Select
        {...{
          value: displayValue,
          onSearch: searchKey ? onSearch : undefined,
          notFoundContent: notFoundContentValidation,
          disabled: disabled || isLocked,
        }}
        showSearch
        dropdownRender={(menu) => (
          <>
            {menu}
            {isFetching && !isLoading ? (
              <span className="flex w-full px-2.5 py-2 text-gray-300">
                Loading...
              </span>
            ) : null}
          </>
        )}
        filterOption={(input, option) => getFilterOption(input, option)}
        onBlur={() => {
          if (createOption) {
            handleCreate()
          }
        }}
        onChange={handleChange}
        onPopupScroll={infiniteScroll ? handlePopupScroll : undefined}
        options={data}
        popupMatchSelectWidth={false}
        ref={selectRef}
        suffixIcon={<CaretDown className="text-base" weight="bold" />}
        {...props}
        className={cn(
          'w-full [&.ant-select-disabled_.ant-select-selection-item]:!text-gray-700',
          '[&.ant-select_.ant-select-arrow]:border-l-[1.5px] [&.ant-select_.ant-select-arrow]:border-gray-400/60',
          '[&.ant-select_.ant-select-arrow]:-mt-2.5 [&.ant-select_.ant-select-arrow]:h-5 [&.ant-select_.ant-select-arrow]:pl-2',
          {
            '[&_.ant-select-selector]:!border-red-600': hasError,
          },
          className
        )}
      />
      {hasError && <ErrorLabel>{errorLabel}</ErrorLabel>}
    </div>
  )
}
